// Výběr termínu zkušebního tréninku (90 minut) z rezervačního systému.
// 2. 10. 2026 (mockup „Výběr termínu na měsíc“): celý měsíc najednou, listovat jde
// o měsíc dopředu, u každého dne počet volných termínů, dole velké shrnutí data,
// které sales řekne klientce. Používá krok 3 hovoru a přesun zkušebního.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, Sunset, Check, Info, CalendarCheck2 } from "lucide-react";
import { RozbalovaciVyber, Zapinatko } from "../ui.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { salesGet, dnesIso, addDays, pondeli, casBezNuly } from "./salesApi.js";
import { Pil, Oznameni } from "./salesUi.jsx";
import MesicniKalendar, { denSlovy, volnychText } from "./MesicniKalendar.jsx";

const NA_TELEFONU = 6;
function plus90(cas) { const [h, m] = String(cas || "0:0").split(":").map(Number); const k = h * 60 + m + 90; return `${Math.floor(k / 60)}:${String(k % 60).padStart(2, "0")}`; }

export default function TerminPicker({ lokalita, trenerVychozi = "", value, onChange, vychoziOdpoledne = true, zacitPristimTydnem = true }) {
  const dnes = dnesIso();
  const uzka = useUzkaObrazovka();
  const odKdy = zacitPristimTydnem ? pondeli(addDays(dnes, 7)) : addDays(dnes, 1);
  const [mesic, setMesic] = useState(() => String(value?.datum || odKdy).slice(0, 7));
  const [trener, setTrener] = useState(trenerVychozi || "");
  const [odpoledne, setOdpoledne] = useState(vychoziOdpoledne);
  const [data, setData] = useState(null);
  const [nacitam, setNacitam] = useState(false);
  const [chyba, setChyba] = useState("");
  const [den, setDen] = useState(value?.datum || "");
  const [vse, setVse] = useState(false);
  const denRef = useRef(null);
  // Na telefonu jsou časy pod kalendářem, po klepnutí na den k nim sjet.
  const vyberDen = (d) => {
    setDen(d);
    if (uzka) setTimeout(() => denRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  useEffect(() => { setTrener(trenerVychozi || ""); }, [trenerVychozi]);
  useEffect(() => {
    let zruseno = false;
    setChyba(""); setNacitam(true);
    salesGet("terminy", { mesic, lokalita, trener, odpoledne: odpoledne ? "1" : "" })
      .then((d) => {
        if (zruseno) return;
        setData(d);
        if (d.mesic !== mesic) setMesic(d.mesic);
        if (!(den && d.sloty[den]?.length)) {
          const sVolnem = d.dny.filter((x) => x.pocet > 0 && !x.minulost);
          const prvni = sVolnem.find((x) => x.datum >= odKdy) || sVolnem[0];
          setDen(prvni ? prvni.datum : "");
        }
      })
      .catch((e) => !zruseno && setChyba(e.message))
      .finally(() => !zruseno && setNacitam(false));
    return () => { zruseno = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesic, lokalita, trener, odpoledne]);
  useEffect(() => { setVse(false); }, [den]);

  const sloty = useMemo(() => (data && den ? data.sloty[den] || [] : []), [data, den]);
  const viditelne = uzka && !vse ? sloty.slice(0, NA_TELEFONU) : sloty;
  const treneri = [{ key: "", label: "Všichni" }, ...((data?.treneri || []).map((t) => ({ key: String(t.id), label: t.prioritni ? `${t.jmeno}, přednostně` : t.jmeno })))];
  const jeVybrany = (s) => value && value.datum === den && value.cas === s.cas && String(value.trener_id) === String(s.trener_id);
  const nazevPobocky = (kod) => (data?.lokality || []).find((l) => l.kod === kod)?.nazev || "";
  const popisDne = (d) => {
    if (d.svatek) return { dostupny: d.pocet > 0, text: "svátek", ton: "svatek" };
    return d.pocet > 0 ? { dostupny: true, text: volnychText(d.pocet), kratce: String(d.pocet), ton: "volno" } : { dostupny: false, text: "plno", ton: "plno" };
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-2 sm:grid-cols-2">
        <RozbalovaciVyber stitek="Trenér" items={treneri} value={trener} onChange={setTrener} aria-label="Filtr trenéra" />
        <Zapinatko zapnuto={odpoledne} onZmena={setOdpoledne} popisek="Jen odpoledne" ikona={Sunset} />
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
        <div className="space-y-3">
          <div className={nacitam && data ? "opacity-70" : ""} aria-busy={nacitam}>
            <MesicniKalendar data={data} mesic={mesic} onMesic={setMesic} vybrany={den} onVyber={vyberDen} popisDne={popisDne} nacitam={nacitam} />
          </div>
        </div>
        <div className="space-y-3">
          {den && (
            <div ref={denRef} className="flex scroll-mt-4 flex-wrap items-center gap-2 rounded-xl bg-brand-100 px-3 py-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-brand-deep"><CalendarDays className="h-[18px] w-[18px]" /></span>
              <span className="min-w-0 flex-1 basis-[calc(100%-3rem)] text-[17px] font-bold text-brand-950 sm:basis-0">{denSlovy(den)}</span>
              <span className="rounded-full bg-white px-2.5 py-1 text-[12px] font-bold text-brand-deep">{volnychText(sloty.length)}</span>
              <span className="rounded-full bg-white px-2.5 py-1 text-[12px] font-bold text-brand-deep">každý 90 minut</span>
            </div>
          )}
          {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
          {!data && !chyba && <div className="h-32 animate-pulse rounded-xl bg-brand-light" />}
          {data && !den && (
            <Oznameni ton="info">V tomhle měsíci není volný termín{trener ? " u vybraného trenéra" : ""}{odpoledne ? " odpoledne" : ""}. Zkus další měsíc{trener ? ", jiného trenéra" : ""}{odpoledne ? " nebo vypni Jen odpoledne" : ""}.</Oznameni>
          )}
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
            {viditelne.map((s) => {
              const zap = jeVybrany(s);
              return (
                <button key={`${s.cas}-${s.trener_id}`} type="button" onClick={() => onChange({ datum: den, cas: s.cas, trener_id: s.trener_id, trener: s.trener, lokalita: data.lokalita })}
                  aria-pressed={zap}
                  className={`flex min-h-[56px] items-center gap-3 rounded-xl border px-3.5 py-2 text-left ${zap ? "border-2 border-brand bg-brand-50" : "border-slate-200 bg-white"}`}>
                  <span className="w-14 text-lg font-bold tabular-nums text-slate-900">{casBezNuly(s.cas)}</span>
                  <span className="min-w-0 flex-1 lg:flex lg:flex-wrap lg:items-center lg:gap-x-3">
                    <span className="flex flex-wrap items-center gap-1.5 text-[15px] font-bold text-slate-900">{s.trener}{s.prioritni && <Pil ton="zlata" casti="přednostně" mala />}</span>
                    <span className="block text-[13px] tabular-nums text-slate-500">{casBezNuly(s.cas)} až {casBezNuly(s.konec)}</span>
                  </span>
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${zap ? "border-brand bg-brand text-white" : "border-slate-300"}`}>{zap && <Check className="h-3.5 w-3.5" />}</span>
                </button>
              );
            })}
          </div>
          {sloty.length > viditelne.length && (
            <button type="button" className="btn-secondary w-full justify-center" onClick={() => setVse(true)}>
              Ukázat {sloty.length - viditelne.length === 1 ? "další 1 termín" : `další ${sloty.length - viditelne.length} ${sloty.length - viditelne.length <= 4 ? "termíny" : "termínů"}`}
            </button>
          )}
        </div>
      </div>
      {value?.datum && (
        <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-700"><CalendarCheck2 className="h-5 w-5" /></span>
          <span className="min-w-0">
            <span className="block text-[18px] font-bold leading-snug text-emerald-900 sm:text-[20px]">{denSlovy(value.datum)} v {casBezNuly(value.cas)}, {value.trener}</span>
            <span className="block text-[13px] text-emerald-800">{[nazevPobocky(value.lokalita), `${casBezNuly(value.cas)} až ${plus90(value.cas)}`].filter(Boolean).join(", ")}</span>
          </span>
        </div>
      )}
      <div className="flex items-start gap-2 text-[13px] leading-snug text-slate-500">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <span>V jedné pobočce nikdy nezačínají dva zkušební tréninky ve stejný čas, další může začít nejdřív o 30 minut později nebo dřív.</span>
      </div>
    </div>
  );
}
